import React from "react"
import { Link } from "gatsby"
import { motion, useReducedMotion } from "framer-motion"
import Image from "./image"
import FooterLinks from "./footerLinks"
import ToptalBadge from "./toptalBadge"
import vueboardChallenges from "../images/vueboard-challenges.png"
import "./home.css"

const emailAddress = "rahul.vagadiya@gmail.com"
const calendlyUrl = "https://calendly.com/rahulv/30min"
const emailUrl =
  "mailto:" +
  emailAddress +
  "?subject=" +
  encodeURIComponent("Building a product together")

const selectedWork = [
  {
    name: "Inkse",
    href: "https://www.inkse.com",
    image: "inkse.png",
    imageAlt: "Inkse custom garment design interface",
    title: "Made custom fashion design part of the purchase flow.",
    body:
      "Built an interactive 3D garment-customisation experience where customers could add designs across a garment, alongside the commerce platform and product-configuration work needed to make the experience purchasable.",
    details:
      "Architecture · Next.js · Three.js · Shopify · serverless workflows",
  },
  {
    name: "Remo",
    href: "https://remo.co/",
    image: "remo.png",
    imageAlt: "Remo virtual event interface",
    title: "Turned complex collaboration and admin work into product features.",
    body:
      "Delivered features including map-based user location and self-service SSO configuration, moving a previously manual customer setup into the product while working closely with the product team.",
    details:
      "Real-time systems · SSO · cloud functions · testing and observability",
  },
  {
    name: "Sophion ViewPoint",
    href: "https://sophion.com/products/qube-384/",
    image: "viewpoint.png",
    imageAlt: "Sophion ViewPoint scientific software interface",
    title: "Built a clearer, safer experiment setup experience.",
    body:
      "Worked on the ViewPoint interface for Sophion's Qube 384: an assay-based GUI designed to guide experiment setup, reduce human error, and give operators more control.",
    details: "Product UI · TypeScript · component systems · state management",
  },
  {
    name: "Stayhopper",
    href: "https://stayhopper.com",
    image: "stayhopper.png",
    imageAlt: "Stayhopper booking product interface",
    title:
      "Built the end-to-end customer and operational apps of a hotel booking platform.",
    body:
      "Built an end-to-end hotel micro-booking suite: the booking journey, backend APIs, payment integration, availability and pricing, and CMS tools for staff and hotel operators.",
    details: "End-to-end delivery · payments · APIs · operations tooling",
  },
]

const personalProjects = [
  {
    name: "Vueboard",
    href: "https://vueboard-app.web.app/",
    kind: "vueboard",
    imageAlt:
      "Vueboard challenges board with a fitness competition countdown, pushups, reading, water, and deep work cards",
    body:
      "A small personal dashboard for challenges, routines, reminders, notes, and the progress I want to keep visible.",
  },
  {
    name: "Whadapp",
    href: "https://whadapp.com",
    image: "whadapp.png",
    imageAlt: "Whadapp social product",
    body:
      "A deliberately simple social network for sharing posts with people who follow you.",
  },
  {
    name: "Blunk",
    href: "https://theblunks.com",
    image: "blunks.png",
    imageAlt: "Blunk link-collection product",
    body: "A simple way to collect, organise, and share a useful set of links.",
  },
  {
    name: "Photare",
    href: "https://photare.com",
    image: "photare.png",
    imageAlt: "Photare image-sharing product",
    body:
      "A fast, no-account way to turn an image into a shareable link and understand how it travels.",
  },
  {
    name: "Holaa",
    href: "http://holaachat.com/",
    image: "holaachat.png",
    imageAlt: "Holaa anonymous public chat product",
    body:
      "Lightweight, anonymous public conversation without accounts or a persistent history.",
  },
]

const principles = [
  {
    title: "Make the next step safe and obvious.",
    body:
      "A happy path is easy to demo. The quality of a product shows in what it does when a payment fails, a request duplicates, or a person gets interrupted.",
    slug: "quality-is-visible-in-recovery",
  },
  {
    title: "Treat a prototype as a question, not a finish line.",
    body:
      "A polished prototype can create commitment before security, reliability, and operating realities have earned it. I use early builds to learn what must be true before scaling them.",
    slug: "a-convincing-prototype-a-dangerous-milestone",
  },
  {
    title: "Use AI to improve judgment, not just output.",
    body:
      "When building gets cheaper, choosing the right problem and the smallest worthwhile solution becomes more valuable - not less.",
    slug: "the-new-edge-is-selection",
  },
]

const process = [
  {
    title: "Find the sharpest first bet",
    body:
      "Get clear on the customer, the problem, and what needs proving. Then cut the rest.",
  },
  {
    title: "Put it in front of people",
    body:
      "Ship the smallest useful version early and let real behaviour decide what earns the next investment.",
  },
  {
    title: "Build for the next stage",
    body:
      "The first release should be dependable from day one and ready to grow without starting over.",
  },
]

const testimonials = [
  {
    quote:
      "Rahul is outcome driven and works extremely well in an Agile project.",
    name: "Paul Schmidt",
    role: "Chief Data and Analytics Strategist, MPower MSL",
    href: "https://www.vrbrosllc.com/",
    source: "Published recommendation",
  },
  {
    quote:
      "A very strong full-stack developer, with a great eye for detail and beautiful, functional user interfaces.",
    name: "Iraklis Alexopoulos",
    role: "Published on LinkedIn",
    href: "https://ae.linkedin.com/in/rahulvagadiya",
    source: "Read recommendation",
  },
]

const reveal = (reduced, delay = 0) =>
  reduced
    ? {}
    : {
        initial: { opacity: 0, y: 22 },
        animate: { opacity: 1, y: 0 },
        transition: {
          duration: 0.58,
          delay,
          ease: [0.22, 1, 0.36, 1],
        },
      }

const ProjectVisual = ({ project, className }) => {
  if (project.kind === "vueboard") {
    return (
      <img
        className={className + " vueboard-preview"}
        src={vueboardChallenges}
        alt={project.imageAlt}
        loading="lazy"
        decoding="async"
      />
    )
  }

  return (
    <Image
      alt={project.imageAlt}
      fileName={project.image}
      className={className}
      fluid
    />
  )
}

const Home = ({ links }) => {
  const railRef = React.useRef(null)
  const workRailRef = React.useRef(null)
  const shouldReduceMotion = useReducedMotion()
  const heroAnimation = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 18 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
      }

  const movePersonalRail = direction => {
    if (!railRef.current) return

    railRef.current.scrollBy({
      left: railRef.current.clientWidth * 0.78 * direction,
      behavior: shouldReduceMotion ? "auto" : "smooth",
    })
  }

  const moveWorkRail = direction => {
    if (!workRailRef.current) return

    workRailRef.current.scrollBy({
      left: workRailRef.current.clientWidth * 0.78 * direction,
      behavior: shouldReduceMotion ? "auto" : "smooth",
    })
  }

  return (
    <div className="home">
      <motion.section
        className="home-hero"
        aria-labelledby="home-title"
        {...heroAnimation}
      >
        <div className="home-hero__copy">
          <h1 id="home-title">Rahul Vagadiya</h1>
          <p className="home-hero__headline">
            I help early-stage teams turn ambiguous ideas into reliable web and
            AI-enabled products.
          </p>
          <p className="home-hero__body">
            Product thinking, technical direction, and hands-on full-stack
            delivery - from choosing the right first version to shipping the
            core experience.
          </p>
          <div className="home-hero__actions">
            <a
              className="home-button"
              href={calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Start a conversation
            </a>
            {/* <a className="home-text-link" href="#selected-work">
              See selected work <span aria-hidden="true">↓</span>
            </a> */}
          </div>
          <p className="home-hero__note">
            Building something early, complicated, or stuck?{" "}
            <a href={emailUrl}>Email me directly.</a>
          </p>
        </div>
        <aside className="home-hero__aside" aria-label="Selected experience">
          <Image
            alt="Rahul Vagadiya"
            fileName="rahul.png"
            className="home-hero__portrait rv-rounded-full"
          />
          <p>Selected experience across</p>
          <ul>
            <li>Early-stage product development</li>
            <li>Real-time and operational software</li>
            <li>Commerce, bookings, AI, and other integrations</li>
          </ul>
          <ToptalBadge className="toptal-badge--hero" />
        </aside>
      </motion.section>

      {/* <section className="home-proof" aria-label="Selected teams">
        <span>Selected teams and products</span>
        <span>Inkse</span>
        <span>Sophion</span>
        <span>Remo</span>
        <span>Stayhopper</span>
        <span>Musafir</span>
      </section> */}

      <motion.section
        className="home-section home-process"
        aria-labelledby="process-title"
        {...reveal(shouldReduceMotion)}
      >
        <div className="home-section__intro">
          <h2 id="process-title">Building with founders.</h2>
          <p>
            How I assist in turning a useful insight into something people can
            use, learn from, and keep using.
          </p>
        </div>
        <ol className="process-list">
          {process.map((step, index) => (
            <li key={step.title}>
              <span>0{index + 1}</span>
              <div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </motion.section>

      <motion.section
        className="home-section home-section--principles"
        aria-labelledby="principles-title"
        {...reveal(shouldReduceMotion)}
      >
        <div className="home-section__intro">
          <h2 id="principles-title">
            My thoughts on product, AI, and building.
          </h2>
          <p>
            Short notes from my own work - on the decisions that make a product
            trustworthy, useful, and worth continuing to build.
          </p>
        </div>
        <div className="principle-list">
          {principles.map((principle, index) => (
            <motion.article
              className="principle"
              key={principle.slug}
              {...reveal(shouldReduceMotion, index * 0.06)}
            >
              <h3>
                <Link to={"/thoughts/" + principle.slug + "/"}>
                  {principle.title}
                </Link>
              </h3>
              <p>{principle.body}</p>
              <Link
                className="home-text-link"
                to={"/thoughts/" + principle.slug + "/"}
              >
                Read the note <span aria-hidden="true">→</span>
              </Link>
            </motion.article>
          ))}
        </div>
        <Link className="home-all-thoughts" to="/thoughts/">
          All thoughts <span aria-hidden="true">→</span>
        </Link>
      </motion.section>

      <motion.section
        className="home-now"
        aria-labelledby="now-title"
        {...reveal(shouldReduceMotion)}
      >
        <div className="home-now__copy">
          <p className="home-now__label">Now</p>
          <h2 id="now-title">Building Vueboard.</h2>
          <p>
            A small personal dashboard for challenges, routines, reminders,
            notes, and the progress I want to keep visible.
          </p>
          <a
            className="home-now__link"
            href="https://vueboard-app.web.app/"
            target="_blank"
            rel="noopener noreferrer"
          >
            See Vueboard <span aria-hidden="true">→</span>
          </a>
        </div>
        <ProjectVisual
          project={personalProjects[0]}
          className="home-now__visual"
        />
      </motion.section>

      <motion.section
        className="home-section home-section--personal"
        aria-labelledby="personal-projects-title"
        {...reveal(shouldReduceMotion)}
      >
        <div className="home-section__intro home-section__intro--wide">
          <h2 id="personal-projects-title">
            Things I&apos;ve made for myself.
          </h2>
          <p>
            Taking an idea all the way through: the behaviour, the edge cases,
            and the details that make it useful.
          </p>
        </div>
        <div className="personal-rail__header">
          <p>Browse projects</p>
          <div className="personal-rail__controls">
            <button
              type="button"
              onClick={() => movePersonalRail(-1)}
              aria-label="Show previous projects"
            >
              ←
            </button>
            <button
              type="button"
              onClick={() => movePersonalRail(1)}
              aria-label="Show next projects"
            >
              →
            </button>
          </div>
        </div>
        <div
          className="personal-rail"
          ref={railRef}
          aria-label="Personal projects"
        >
          {personalProjects.map((project, index) => (
            <motion.a
              className="personal-card"
              href={project.href}
              key={project.name}
              target="_blank"
              rel="noopener noreferrer"
              {...reveal(shouldReduceMotion, index * 0.04)}
              whileHover={shouldReduceMotion ? undefined : { y: -3 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <ProjectVisual
                project={project}
                className="personal-card__visual"
              />
              <h3>{project.name}</h3>
              <p>{project.body}</p>
              <span className="personal-card__arrow" aria-hidden="true">
                ↗
              </span>
            </motion.a>
          ))}
        </div>
      </motion.section>

      <motion.section
        className="home-section home-case-study"
        aria-labelledby="inkse-case-study-title"
        {...reveal(shouldReduceMotion)}
      >
        <div className="home-section__intro home-section__intro--wide">
          <h2 id="inkse-case-study-title">
            Inkse - making custom fashion design purchasable.
          </h2>
          <p>
            A closer look at a product where the visual experience and the
            commerce system had to work as one.
          </p>
        </div>
        <div className="case-study">
          <div className="case-study__image">
            <Image
              alt="Inkse interactive garment customisation"
              fileName="inkse.png"
              fluid
            />
          </div>
          <div className="case-study__content">
            <article>
              <h3>The problem</h3>
              <p>
                Customers needed to see and customise a fashion line before they
                could confidently buy it. A conventional product page could not
                carry that experience.
              </p>
            </article>
            <article>
              <h3>My role</h3>
              <p>
                I owned the full-stack architecture and delivery across the 3D
                design experience, commerce integration, product configuration,
                and asynchronous workflows.
              </p>
            </article>
            <article>
              <h3>A key decision</h3>
              <p>
                I successfully demonstrated via a PoC the high-end capabilities
                of rendering 3D garments with multiple add-ons (embroidery,
                text, images) across garment placements (back, front, label) all
                baked together with a granular pricing and discounts based
                checkout.
              </p>
            </article>
            <article>
              <h3>The result</h3>
              <p>
                Customers could design, preview, configure, and purchase custom
                garments in one product flow, with real-time updates for
                uploads, approvals, and file-processing work.
              </p>
            </article>
          </div>
        </div>
      </motion.section>


      <motion.section
        className="home-section home-section--work"
        id="selected-work"
        aria-labelledby="selected-work-title"
        {...reveal(shouldReduceMotion)}
      >
        <div className="home-section__intro home-section__intro--wide">
          <h2 id="selected-work-title">
            A few things I&apos;ve built or helped build.
          </h2>
          <p>
            Product work with teams where I contributed directly to the customer
            experience, the technical foundation, or both.
          </p>
        </div>
        <div className="work-rail__header">
          <p>Browse client work</p>
          <div className="work-rail__controls">
            <button
              type="button"
              onClick={() => moveWorkRail(-1)}
              aria-label="Show previous client projects"
            >
              ←
            </button>
            <button
              type="button"
              onClick={() => moveWorkRail(1)}
              aria-label="Show next client projects"
            >
              →
            </button>
          </div>
        </div>
        <div
          className="work-rail"
          ref={workRailRef}
          aria-label="Client projects"
        >
          {selectedWork.map((work, index) => (
            <motion.article
              className="work-card"
              key={work.name}
              {...reveal(shouldReduceMotion, index * 0.05)}
              whileHover={shouldReduceMotion ? undefined : { y: -4 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <Image
                alt={work.imageAlt}
                fileName={work.image}
                className="work-card__image"
                fluid
              />
              <h3>
                <a href={work.href} target="_blank" rel="noopener noreferrer">
                  {work.name}
                </a>
              </h3>
              <h4>{work.title}</h4>
              <p>{work.body}</p>
              <p className="work-card__details">{work.details}</p>
            </motion.article>
          ))}
        </div>
      </motion.section>

      <motion.section
        className="home-section home-testimonials"
        aria-labelledby="testimonials-title"
        {...reveal(shouldReduceMotion)}
      >
        <div className="home-section__intro">
          <h2 id="testimonials-title">A couple of kind words.</h2>
        </div>
        <div className="testimonial-list">
          {testimonials.map((testimonial, index) => (
            <motion.figure
              className="testimonial"
              key={testimonial.name}
              {...reveal(shouldReduceMotion, index * 0.08)}
            >
              <blockquote>“{testimonial.quote}”</blockquote>
              <figcaption>
                <strong>{testimonial.name}</strong>
                <span>{testimonial.role}</span>
                <a
                  href={testimonial.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {testimonial.source} <span aria-hidden="true">↗</span>
                </a>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </motion.section>

      <section className="home-cta" aria-labelledby="home-cta-title">
        <h2 id="home-cta-title">Have a product problem worth solving?</h2>
        <p>
          I work with a small number of teams that need a hands-on technical
          partner to bring clarity and momentum to an important product.
        </p>
        <div className="home-cta__actions">
          <a
            className="home-button"
            href={calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Book a 30-minute call
          </a>
          <a className="home-text-link" href={emailUrl}>
            Or send an email <span aria-hidden="true">→</span>
          </a>
        </div>
      </section>

      <div className="home-footer">
        <FooterLinks links={links} />
        <Link to="/music/" className="home-footer__music">
          I also make music <span aria-hidden="true">→</span>
        </Link>
      </div>
    </div>
  )
}

export default Home
